import React, { useState } from 'react';
import { statusBadgeText } from '../utils/BadgeEssay';
import moment from 'moment';
import { FiEdit3, FiEye, FiUpload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import { useClient } from 'jsonapi-react';
import usePermission from '@/hooks/usePermission';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardBodyHeaderContent, CardContent, CardFooter, CardKpi, CardKpiGrid, CardKpiTitle, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function EssayMonitoringCardMobile({ student, essay, currentUser }) {
  const navigate = useNavigate();
  const statusBadgeColor = {
    accepting_proposal: 'attention',
    close_for_submissions: 'neutral',
    processing: 'warning',
    unreviewed: 'success',
    reviewed: 'success',
    annulled: 'danger',
    annulled_by_teacher: 'danger ',
    minimum_length_not_satisfied: 'danger',
    failed_to_process: 'danger',
    reviewing: 'warning',
    not_sent: 'neutral',
    ocr_review: 'warning',
    sending_by_teacher: 'warning'
  };
  const statusClass = statusBadgeColor?.[student.status];
  const badgeText = statusBadgeText?.[student.status];
  const [openWarning, setOpenWarning] = useState(false);
  const client = useClient();

  const canSend = usePermission('Enviar redação', 'Redação');

  const handleReviewEssay = () => {
    if (student.status === 'unreviewed') {
      client.mutate(['essays', essay.id, 'essay_students', student.id], { status: 'reviewing' });
    }
    navigate(`/redacao/correcao/${student.id}/${essay.id}?modo=correcao`);
    setOpenWarning(false);
  };

  function renderAction() {
    const { status, id, corrector, user } = student;

    const viewEnabled =
      status === 'reviewed' || // corrigida pelo professor
      status === 'unreviewed' || // correção por IA
      status === 'annulled_by_teacher' || // anulada pelo professor
      status === 'reviewing' || // está sendo corrigida
      status === 'annulled'; // anulada

    const reviewEnabled =
      status === 'unreviewed' || // correção por IA
      (status === 'reviewing' && corrector.id === currentUser); // corrigindo e quem está corrigindo é o usuário atual

    const reachedSubmissionLimit = () => {
      const limit = essay['allow-multiple-submissions'] ? 2 : 1;
      return student['submission_order'] >= limit;
    };

    const isInvalid = () => ['annulled', 'annulled_by_teacher', 'minimum_length_not_satisfied'].includes(student.status);

    const sendDisabled =
      !canSend || // não tem permissão
      (reachedSubmissionLimit() && status !== 'ocr_review') || // já enviou a quantidade permitida
      status === 'reviewing' || // está sendo corrigida
      status === 'processing' || // está sendo processada
      (isInvalid() && reachedSubmissionLimit()) || // é inválida e já enviou a quantidade permitida
      status === 'close_for_submissions' || // prazo de envio encerrado
      essay.status === 'close_for_submissions' || // prazo de envio encerrado (status geral da redação)
      status === 'unreviewed'; // correção por IA

    return (
      <div className="flex w-full flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/redacao/enviar/${essay.id}/${user?.id}`)}
          disabled={sendDisabled}
        >
          <ButtonIcon>
            <FiUpload />
          </ButtonIcon>{' '}
          <span style={{ minWidth: '82px', fontSize: '12px' }}>Enviar redação</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/redacao/correcao/${id}/${essay.id}?modo=visualizar`)}
          disabled={!viewEnabled}
        >
          <ButtonIcon>
            <FiEye />
          </ButtonIcon>{' '}
          <span style={{ fontSize: '12px' }}>Visualizar</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (status === 'reviewing' && corrector.id === currentUser) {
              navigate(`/redacao/correcao/${id}/${essay.id}?modo=correcao`);
            } else {
              setOpenWarning(true);
            }
          }}
          disabled={!reviewEnabled}
        >
          <ButtonIcon>
            <FiEdit3 />
          </ButtonIcon>{' '}
          <span style={{ fontSize: '12px' }}>Corrigir</span>
        </Button>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <CardBody>
            <CardBodyHeader>
              <CardBodyHeaderContent>
                <CardTitle title={student?.user?.name}>{student?.user?.name}</CardTitle>
              </CardBodyHeaderContent>

              <Badge
                title={badgeText}
                className={statusClass}
                size="xs"
              >
                {badgeText}
              </Badge>
            </CardBodyHeader>

            <CardBodyContent>
              <CardKpiGrid>
                <CardKpi>
                  <CardKpiTitle>Entrega</CardKpiTitle>
                  {student?.['delivery-date'] ? moment(student?.['delivery-date']).format('DD/MM/YYYY') : '-'}
                </CardKpi>
                <CardKpi>
                  <CardKpiTitle>Nota</CardKpiTitle>
                  {student?.grade ?? '-'}
                </CardKpi>
              </CardKpiGrid>

              <CardKpi>
                <CardKpiTitle>Corrigido por</CardKpiTitle>
                {student?.corrector?.name ?? '-'}
              </CardKpi>
            </CardBodyContent>
          </CardBody>

          <CardFooter>{renderAction()}</CardFooter>
        </CardContent>
      </Card>

      <ModalWarning
        show={openWarning}
        onClose={() => setOpenWarning(false)}
        titleFirstButton="Não"
        titleSecondButton="Sim"
        title={`Deseja mesmo corrigir essa redação?`}
        description="Outros professores não poderão corrigi-la."
        onSubmitFirstButton={() => setOpenWarning(false)}
        onSubmitSecondButton={() => handleReviewEssay()}
      />
    </>
  );
}
